import {
    Box, Button, Container, Dialog, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputAdornment, OutlinedInput, Pagination, Stack, Typography
} from "@mui/material";
import Iconify from "src/components/iconify";
import { useSettingsContext } from "src/components/settings";
import { useBoolean } from "src/hooks/use-boolean";
import { useLocales } from "src/locales";
import WorkspaceCreate from "../workspace-create";
import { WorkspaceList, useGetWorkspacesQuery, useDeleteWorkspaceMutation } from "src/context/api/workspaces";
import workspacesApi, { useGetClientsNameQuery, useInvitationsQuery, useUpdateWorkspaceMutation } from "src/context/api/workspaces/api";
import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setCurrentWorkspace, useCurrentWorkspace } from "src/context/reducers/app-settings";
import FileManagerFolderItem from "src/sections/file-manager/file-manager-folder-item";
import FileManagerFolderItemPending from "src/sections/file-manager/file-manager-folder-item-pending";
import { useCurrentUserQuery } from "src/context/api/auth";
import { enqueueSnackbar } from "notistack";
import { useRouter } from "src/routes/hooks";
import { setFilterAdvertizerPLA, setFilterAveragePositionPLA, setFilterBrandPLA, setFilterCountry, setFilterDomainPLA, setFilterImpressionSharePLA, setFilterKeyword, setFilterPricingRangePLA, setFilterRatingRangePLA, setNotificationBrandInfringement, setNotificationBrandInfringementNotification, setNotificationBrandInfringementTXT, setNotificationBrandInfringementNotificationTXT, setFilterLoadedKeyword, setNotificationBrandInNonBrandCampaignsFilterEnabled, setNotificationBrandInNonBrandCampaigns } from "src/context/reducers/filter-settings/filterSlice";
import Cockpit from "src/sections/overview/dashboards/views/cockpit";
import { SplashScreen } from "src/components/loading-screen";
import { useSelector } from 'react-redux';

export default function WorkspaceGeneralView() {
    const { t } = useLocales();
    const newWorkspaceModal = useBoolean(false);
    const workspaces = useGetWorkspacesQuery(null);
    const [searchQuery, setSearchQuery] = useState("");
    const invitations: any = useInvitationsQuery(null);
    const user: any = useCurrentUserQuery();
    const [localWorkspaces, setLocalWorkspaces] = useState<any>(workspaces?.data || []);
    const role = user?.data?.role;
    const demoMode = useSelector((state: any) => state.filters.demoMode);
    const router = useRouter();

    useEffect(() => {
        if (["admin", "brand specialist"].includes(role) && location?.pathname?.split("/")[2] === "add") {
            newWorkspaceModal.onTrue()
        }
    }, [location.pathname])

    useEffect(() => {
        if (workspaces?.data) {
            setLocalWorkspaces(workspaces.data);
        }
    }, [workspaces?.data]);

    const handleInvitation = (newInvitation: any) => {
        const oldArray = localWorkspaces

        const newWorkspace = oldArray.find((workspace: any) => workspace.id === newInvitation.workspaceId)

        // remove the invitation from the array
        if (newInvitation.toDelete) {
            setLocalWorkspaces((prevWorkspaces: any[]) => {
                return prevWorkspaces.map((workspace: any) =>
                    workspace.id === newInvitation.workspaceId
                        ? { ...workspace, invitations: workspace.invitations.filter((invitation: any) => invitation.id !== newInvitation.id) }
                        : workspace
                );
            });

        }

        else if (newInvitation.status === 'PENDING') {
            const oldArray = localWorkspaces
            const newWorkspace = oldArray.find((workspace: any) => workspace.id === newInvitation.workspaceId)
            const invitObj = {
                email: newInvitation.email,
                id: newInvitation.id,
                firstname: newInvitation.firstname,
                lastname: newInvitation.lastname,
                status: 'PENDING'
            }
            setLocalWorkspaces((prevWorkspaces: any[]) => {
                return prevWorkspaces.map((workspace: any) =>
                    workspace.id === newInvitation.workspaceId
                        ? { ...workspace, invitations: [...workspace.invitations, invitObj] }
                        : workspace
                );
            });
        }
    }

    const handleWorkspaceUpdate = (updatedWorkspace: any) => {
        const oldArray = localWorkspaces
        const newWorkspace = oldArray.find((workspace: any) => workspace.id === updatedWorkspace.id)

        setLocalWorkspaces((prevWorkspaces: any[]) => {
            return prevWorkspaces.map((workspace: any) =>
                workspace.id === updatedWorkspace.id
                    ? { ...workspace, ...updatedWorkspace }
                    : workspace
            );
        });
    };


    const handleSearch = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
    };

    const filteredWorkspaces = useMemo(() => {
        if (!localWorkspaces) return [];
        return localWorkspaces
            .filter((workspace: any) =>
                workspace.name.toLowerCase().includes(searchQuery.toLowerCase())
            )
            .sort((a: any, b: any) => a.name.localeCompare(b.name));
    }, [localWorkspaces, searchQuery]);

    const filteredInvitations = useMemo(() => {
        if (!invitations.data) return [];
        return invitations.data.filter((invitation: any) =>
            invitation?.workspace?.name.toLowerCase().includes(searchQuery?.toLowerCase())
        );
    }, [invitations?.data, searchQuery]);

    const [viewMode, setViewMode]: any = useState<'list' | 'grid' | 'unset'>("unset");

    useEffect(() => {
        if (demoMode) {
            if (viewMode !== "grid") {
                setViewMode("grid");
            }
            return;
        }

        if (viewMode === "unset") {
            if (["admin", "brand specialist"].includes(role)) {
                setViewMode("list");
            } else {
                setViewMode("grid");
            }
        }
    }, [viewMode, demoMode, role]);




    if (viewMode == "unset") {
        return <SplashScreen />
    }

    return (
        <Box height={"100%"}>
            <Box sx={{ height: "100%" }}>
                <Box sx={{ display: 'flex', flexDirection: 'row', justifyContent: "space-between", width: '100%' }}>

                    {viewMode === "grid" ?
                        <FormControl sx={{ display: "flex", flexDirection: 'row', justifyContent: 'space-between', width: '100%' }}>
                            <Box px={'24px'}>
                                <OutlinedInput
                                    id="outlined-adornment-amount"
                                    placeholder="Search..."
                                    size="small"
                                    sx={{backgroundColor: "white"}}
                                    value={searchQuery}
                                    onChange={handleSearch}
                                    startAdornment={<InputAdornment position="start"><Iconify icon={"eva:search-fill"} /></InputAdornment>}
                                />
                            </Box>

                            <Box display="flex" justifyContent="flex-end" mb={2} px={"24px"}>
                                {["admin", "brand specialist"].includes(role) && !demoMode && (
                                    <Stack display={'flex'} flexDirection={"row"} spacing={1}>
                                        <IconButton
                                            onClick={() => setViewMode('list')}
                                            sx={{
                                                borderRadius: "8px",
                                                bgcolor: viewMode === 'list' ? 'white' : 'transparent',
                                                width: 35,
                                                height: 35,
                                                color: viewMode === 'list' ? 'rgba(51, 60, 71, 1)' : 'rgba(145, 158, 171, 1)',
                                            }}
                                        >
                                            <Iconify icon="solar:list-outline" />
                                        </IconButton>

                                        <IconButton
                                            onClick={() => setViewMode('grid')}
                                            sx={{
                                                borderRadius: 1.5,
                                                bgcolor: viewMode === 'grid' ? 'white' : 'transparent',
                                                color: viewMode === 'grid' ? 'rgba(51, 60, 71, 1)' : 'rgba(145, 158, 171, 1)',
                                                width: 35,
                                                height: 35,
                                            }}
                                        >
                                            <Iconify icon="akar-icons:dot-grid" />
                                        </IconButton>
                                    </Stack>
                                )}
                            </Box>
                        </FormControl>
                        : <Box></Box>
                    }
                </Box>

                {viewMode === "grid"
                    ? <Box px={"24px"}>

                        {filteredInvitations.length !== 0 &&
                            <>
                                <Typography fontSize={17} sx={{ marginTop: 7 }}>
                                    {t('dashboard.pending')}
                                </Typography>
                                <Box sx={{ mt: 2, display: 'flex', }}>
                                    <WorkspacePending invitations={filteredInvitations} />
                                </Box>
                            </>
                        }

                        {filteredWorkspaces.length !== 0 &&
                            <Box sx={{ marginTop: '20px' }}>
                                <Typography fontSize={17} sx={{ marginTop: 7 }}>
                                    {t('dashboard.available')}
                                </Typography>

                                <Grid spacing={2} container sx={{ mt: .6 }}>
                                    {filteredWorkspaces.map((workspace: any, idx: any) => (
                                        <Grid item key={workspace.id} xs={6} sm={4} lg={3} xl={2}>
                                            <WorkspaceCard key={workspace.id} workspace={workspace} onInvitation={handleInvitation} onUpdate={handleWorkspaceUpdate} />
                                        </Grid>
                                    ))}
                                </Grid>
                            </Box>
                        }
                    </Box>
                    : <Cockpit
                        viewMode={viewMode}
                        setViewMode={setViewMode}
                        demoMode={demoMode}
                    />

                }

                <Dialog maxWidth="md" fullWidth open={newWorkspaceModal.value} onClose={() => {
                    newWorkspaceModal.onFalse()
                    router.push("/workspaces")
                }}>
                    <DialogTitle> {t("workspaces.dialog_create.title")}</DialogTitle>
                    <DialogContent>
                        <WorkspaceCreate onSuccess={() => {
                            workspaces.refetch().unwrap().then(() => {
                                newWorkspaceModal.onFalse()
                            });
                        }} onCancel={() => {
                            newWorkspaceModal.onFalse()
                            router.push("/workspaces")
                        }} />
                    </DialogContent>
                </Dialog>

            </Box>
        </Box>
    );
}

function WorkspacePending({ invitations }: any) {

    return (
        <Grid spacing={2} container>
            {invitations.map((invitation: any) => (

                <Grid key={invitation.id} item spacing={1} xs={6} md={2}>
                    <FileManagerFolderItemPending
                        folder={invitation}
                        onDelete={() => {}}
                    />

                </Grid>
            ))

            }
        </Grid>

    );
}

type WorkspaceCardProps = {
    loading?: false;
    workspace: WorkspaceList;
    onUpdate: (item: any) => void;
    onInvitation: (item: any) => void;
} | {
    loading: true;
    workspace: undefined;
    onUpdate: (item: any) => void;
    onInvitation: (item: any) => void;
};

function WorkspaceCard({ workspace, loading, onUpdate, onInvitation }: WorkspaceCardProps) {
    const { t } = useLocales();
    const editWorkspaceModal = useBoolean(false);
    const [workspaceDelete] = useDeleteWorkspaceMutation();
    const currentWorkspace = useCurrentWorkspace();
    const dispatch = useDispatch();
    const [, setSearchParams] = useSearchParams();
    // const [update, updateWorkspace] = useUpdateWorkspaceMutation();
    const [update] = useUpdateWorkspaceMutation();

    const router = useRouter()
    const workspaces = useGetWorkspacesQuery(null);
    const [workspacesData, setWorkspacesData] = useState<any[]>([]);
    const bqTables = useGetClientsNameQuery(null);


    const handleCancel = () => {
        editWorkspaceModal.onFalse()
        router.push("/workspaces")
    }

    const bqTablesData = useMemo(() => {
        if (!bqTables.data) return [];
        return bqTables.data.map((item: any) => ({ label: item, value: item }));
    }, [bqTables.data]);

    const handleDelete = (workspaceId: number) => {
        if (workspace) {
            workspaceDelete(workspace).unwrap()
                .then(() => {
                    dispatch(workspacesApi.util.updateQueryData("getWorkspaces", null, (draft) => {
                        const index = draft.findIndex((ws) => ws.id === workspaceId);
                        if (index !== -1) draft.splice(index, 1);
                    }) as any);
                    enqueueSnackbar('Workspace deleted successfully', {
                        variant: 'success',
                    });
                    if (workspaces?.data) setSearchParams({ workspace: workspaces?.data[0].id.toString() });
                    // workspaces.refetch()
                });
        }
    };

    return (
        <Fragment>
            {workspace &&
                <Grid>
                    <FileManagerFolderItem
                        key={workspace.id}
                        onClick={() => {
                            setSearchParams({ workspace: workspace.id.toString() });
                            dispatch(setFilterKeyword([]));
                            dispatch(setFilterCountry([]));
                            dispatch(setFilterLoadedKeyword([]));
                            dispatch(setNotificationBrandInfringementNotification([]))
                            dispatch(setNotificationBrandInfringementNotificationTXT([]))
                            dispatch(setFilterBrandPLA([]));
                            dispatch(setFilterDomainPLA([]));
                            dispatch(setFilterAdvertizerPLA([]));
                            dispatch(setFilterRatingRangePLA([0, 5]));
                            dispatch(setFilterPricingRangePLA([0, 50000]));
                            dispatch(setFilterImpressionSharePLA([0, 100]))
                            dispatch(setNotificationBrandInNonBrandCampaignsFilterEnabled(false))
                            dispatch(setNotificationBrandInNonBrandCampaigns(false))
                            dispatch(setFilterAveragePositionPLA([0, 50]))
                            dispatch(setCurrentWorkspace(workspace));
                            dispatch(setNotificationBrandInfringement(false))
                            dispatch(setNotificationBrandInfringementTXT(false))

                            router.push("/brand/search-performance/overview?workspace=" + workspace.id)
                        }}

                        folder={workspace}
                        onEdit={editWorkspaceModal.onTrue}
                        onDelete={() => handleDelete(workspace.id)}
                        active={currentWorkspace?.id === workspace.id}
                        bqTables={bqTablesData}
                        onUpdate={onUpdate}
                        onInvitation={onInvitation}
                    />
                </Grid>
            }

            {workspace && workspace.role !== "GUEST" && (
                <Dialog open={editWorkspaceModal.value} maxWidth="md" fullWidth onClose={handleCancel}>
                    <DialogTitle>{workspace.id ? t("workspaces.dialog_edit.title") : t("workspaces.dialog_create.title")}</DialogTitle>
                    <DialogContent>
                        <WorkspaceCreate
                            values={{
                                id: workspace.id,
                                name: workspace.name,
                                showPlaAlert: workspace?.showPlaAlert,
                                showTxtAlert: workspace?.showTxtAlert,
                                url: workspace.url,
                                bqTable: workspace.bqTable,
                                custom: workspace.custom,
                                averageCart: workspace?.averageCart,
                                template: workspace.template,
                                currency: workspace?.currency,
                                isDataReady: workspace?.isDataReady,
                                showSavings: workspace?.showSavings,
                            }}
                            onCancel={handleCancel}
                        />
                    </DialogContent>
                </Dialog>
            )}
        </Fragment>
    );
}
